import { useState, useEffect } from 'react'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { ArrowUp } from 'lucide-react'
import { AppRoutes } from '@/router'
import OvhBadge from '@/components/feature/OvhBadge'
import { LanguageProvider, LocalizationRuntime, minecraftMeta, useLanguage } from '@/localization'

/** On route change, scroll to the hash target (e.g. /#contact) if present,
 *  otherwise reset to the top. Without this, react-router leaves the scroll
 *  position untouched, so anchor links like Contact never move the page. */
function ScrollToTop() {
  const { pathname, hash } = useLocation()
  useEffect(() => {
    if (hash) {
      // Wait a frame so the target route/section is mounted before we look it up.
      const id = hash.slice(1)
      requestAnimationFrame(() => {
        const el = document.getElementById(id)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' })
          return
        }
        window.scrollTo({ top: 0, behavior: 'instant' })
      })
      return
    }
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [pathname, hash])
  return null
}

/** Appears after scrolling down; returns the user to the top. */
function ScrollToTopButton() {
  const [show, setShow] = useState(false)
  const { t } = useLanguage()

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  if (!show) return null

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-50 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-grass-dark text-white shadow-block transition-all hover:-translate-y-0.5 hover:bg-grass-deep"
      aria-label={t('Scroll to top')}
    >
      <ArrowUp size={20} />
    </button>
  )
}

export default function App() {
  const base = import.meta.env.BASE_URL.replace(/\/$/, '') || '/'
  return (
    <BrowserRouter basename={base}>
      <LanguageProvider>
        <LocalizationRuntime meta={minecraftMeta} />
        <ScrollToTop />
        <AppRoutes />
        <ScrollToTopButton />
        <OvhBadge />
      </LanguageProvider>
    </BrowserRouter>
  )
}
